import React, { useState, useEffect } from "react";
import { useQueryClient } from "react-query";
import classNames from "classnames";
import styles from "./SettingsPage.module.css";
import { useSettings } from "./useSettings";
import { Dashboard } from "../DashboardPage/useDashboard";
import moment from "moment";

import { BlobProvider } from "@react-pdf/renderer";
import SettingsPdf from "./SettingsPdf";

import DiamondListItem from "ManagementTeam/Shared/DiamondListItem";
import AddYearModal from "ManagementTeam/Shared/AddYearModal";
import Card from "ManagementTeam/Shared/Card";
import Select from "ManagementTeam/Shared/Select";
import { ReactComponent as SaveButton } from "../assets/Edit_Category_Save.svg";
import { ReactComponent as CancelButton } from "../assets/Edit_Category_Cancel.svg";
import { Header } from "../Shared/Header/Header";
import MonthLink from "ManagementTeam/Shared/MonthLink";
import SettingsIcon from "../assets/Settings_Icon.svg";
import LockIcon from "ManagementTeam/Shared/LockIcon";
import Auth from "shared/@auth";
import DiamondCardModal from "../DashboardPage/DiamondCardModal";
import { ReactComponent as AgendaIcon } from "./Agenda_PDF_Icon.svg";
import { useMemo } from "react";
import { toTitleCase } from "shared/@utils/formatString";
import Button from "shared/Button";
import NotificationInput from "ManagementTeam/Shared/NotificationInput";
import AddUserModal from "./AddUserModal";
import { CircleLoader } from "@wwg/ui";

/**
 * Structural component. Allows the user to manage application settings (diamonds, guests).
 */
export function SettingsPage({ isHideInfo, setToggleInfo }) {
  const { hasPermission } = Auth.useContainer();
  const readOnly = !hasPermission("management-team.admins");
  const hasOwnersPermission = hasPermission("management-team.owners");
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const date = new Date();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  // console.log(selectedYear);
  const [yearList, setYearList] = useState([]);
  // const [data, setData] = useState(null);
  // Mock data name and address
  const [listAdmins, setListAdmins] = useState([]);
  const [listAdminsEditted, setListAdminsEditted] = useState([]);
  // API call
  const {
    data,
    settingsYearPost,
    settingsPut,
    status,
    //refetch: refetchSettings,
    settingsYearActivatePost,
    settingsYearCalculatePost,
    settingsYearImportDiamondsPost,
    getSettings,
    settingsAddUser,
    settingsAddAdminsToNotificationsPost,
    settingsNotificationData,
    settingsNotificationRefetch,
    adminDelete,
  } = useSettings(selectedYear, { enableExtras: true });
  const {
    data: dashboardData,
    userEditPut,
    refetchDashboard,
    swapIBOPost,
    isFetching: isDashboardFetching,
  } = Dashboard.useContainer();

  const [dashboard, setDashboard] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchDashboard(),
        getSettings(selectedYear),
        // Refresh notifications list
        settingsNotificationRefetch && settingsNotificationRefetch(),
        // Also refresh managers and settings PDF queries used on this page
        queryClient.invalidateQueries(["managers"]),
        queryClient.invalidateQueries(["settingsPdf"]),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    if (settingsNotificationData && settingsNotificationData.admins) {
      setListAdmins(settingsNotificationData.admins);
    }
  }, [settingsNotificationData]);
  useEffect(() => {
    setDashboard(
      dashboardData && dashboardData.meetings ? dashboardData.meetings : []
    );
  }, [dashboardData]);

  const [addUserModalOpen, setAddUserModalOpen] = useState(false);

  const [pdfModalIsOpen, setPdfModalIsOpen] = useState(false);
  const [pdfSortSelect, setPdfSortSelect] = useState("Diamond Name");
  const [pdfColumnSelect, setPdfColumnSelect] = useState([]);
  const radioOptions1 = ["Name", "GAR", "Integrated GAR", "Pin Wearing"];
  const radioOptions2 = [
    "Executive Committee",
    "Voting Member",
    "Capital Share",
    "Year Qualified",
  ];

  const [isRefetchingDashData, setIsRefetchingDashData] = useState(false);
  const [isEditInfo, setIsEditInfo] = useState(false);
  const [storeYearSelect, setStoreYearSelect] = useState(
    selectedYear ? selectedYear : currentYear
  );

  const [useYearList, setUseYearList] = useState(null);

  useEffect(() => {
    if (yearList && yearList.length > 0) {
      var _yearList = JSON.parse(JSON.stringify(yearList)).filter(
        (year) => year !== selectedYear
      );
      setUseYearList(_yearList);
    }
  }, [yearList, selectedYear]);

  useEffect(() => {
    let year = selectedYear;
    if (!selectedYear) {
      year = new Date().getFullYear();
    }

    getSettings(year);
  }, [selectedYear]);

  const yearImport = useMemo(() => {
    if (data && data.imports && data.imports.length && selectedYear) {
      return data.imports.find((a) => a.importYear == selectedYear);
    }
    return null;
  }, [data, selectedYear]);

  const [dashData, setDashData] = useState(null);
  useEffect(() => {
    dashboard && dashboard.length > 0 && setDashData(dashboard[0].users);
  }, [dashboard]);

  const [diamondInfoModal, setDiamondInfoModal] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const handleSetDiamond = async (id) => {
    if (dashData) {
      setDiamondInfoModal(null);
      const user = dashData.find((user) => user._id === id);

      let firstName =
        user.profile && user.profile.firstName ? user.profile.firstName : "";
      let lastName =
        user.profile && user.profile.lastName ? user.profile.lastName : "";
      let name = toTitleCase(`${firstName} ${lastName}`);

      const modalData = {
        userId: user._id,
        name: name,
        firstName: firstName,
        lastName: lastName,
        canSwap: user.profileB && user.profileB.firstName ? true : false,
        picture:
          user.profile && user.profile.photoUrl ? user.profile.photoUrl : "N/A",
        email: user.profile && user.profile.email ? user.profile.email : "N/A",
        phoneNumber:
          user.profile && user.profile.phone ? user.profile.phone : "N/A",
        address:
          user.profile && user.profile.address ? user.profile.address : "N/A",
        spouse: user.profile && user.profile.spouse ? user.profile.spouse : "",
        iboNum: user.iboNumber ? user.iboNumber : "",
        birthdate:
          user.profile && user.profile.birthday
            ? moment.utc(user.profile.birthday).format("MMMM DD, YYYY")
            : "",
        anniversary:
          user.profile && user.profile.anniversary
            ? moment.utc(user.profile.anniversary).format("MMMM DD, YYYY")
            : "",
        uplineDiamond:
          user.businessInfo && user.businessInfo.uplineDiamond
            ? user.businessInfo.uplineDiamond
            : "",
        yearDiamondQualified:
          user.businessInfo && user.businessInfo.yearDiamondQualified
            ? user.businessInfo.yearDiamondQualified
            : "",
        yearQualifyingRequirements:
          user.businessInfo &&
          user.businessInfo.qualifyingRequirementsByYear[0] &&
          user.businessInfo.qualifyingRequirementsByYear[0].year
            ? user.businessInfo.qualifyingRequirementsByYear[0].year
            : 0,
        yearQualified: user.businessInfo
          ? user.businessInfo.yearDiamondQualified
          : "",
        gracePeriod:
          user.businessInfo && user.businessInfo.gracePeriod
            ? user.businessInfo.gracePeriod.isGracePeriod
            : "",
        gracePeriodStarted:
          user.businessInfo &&
          user.businessInfo.gracePeriod &&
          user.businessInfo.gracePeriod.startedOn
            ? moment
                .utc(user.businessInfo.gracePeriod.startedOn)
                .format("MMMM DD, YYYY")
            : "",
        familyReunion: user.eventInfo ? user.eventInfo.hasFamilyReunion : "",
        firstFamilyReunion:
          user.eventInfo && user.eventInfo.firstFamilyReunion
            ? user.eventInfo.firstFamilyReunion
            : "",
        familyReunionAttendance:
          user.eventInfo && user.eventInfo.attendanceAtLastFamilyReunion
            ? user.eventInfo.attendanceAtLastFamilyReunion
            : "",
        votingMemberYear: user.businessInfo
          ? user.businessInfo.yearVotingMemberQualified
          : "",
        pinLevel:
          user.businessInfo &&
          user.businessInfo.qualifyingRequirementsByYear[0] &&
          user.businessInfo.qualifyingRequirementsByYear[0].pinWearing
            ? user.businessInfo.qualifyingRequirementsByYear[0].pinWearing
            : "-",
        garPoints:
          user.businessInfo && user.businessInfo.qualifyingRequirementsByYear[0]
            ? user.businessInfo.qualifyingRequirementsByYear[0].garPoints
            : 0,
        integratedGarPoints:
          user.businessInfo && user.businessInfo.qualifyingRequirementsByYear[0]
            ? user.businessInfo.qualifyingRequirementsByYear[0]
                .integratedGarPoints
            : 0,
        diamondRankGar:
          user.businessInfo && user.businessInfo.qualifyingRequirementsByYear[0]
            ? user.businessInfo.qualifyingRequirementsByYear[0].diamondRankGar
            : 0,
        diamondRankWwg:
          user.businessInfo && user.businessInfo.qualifyingRequirementsByYear[0]
            ? user.businessInfo.qualifyingRequirementsByYear[0].diamondRankWWG
            : 0,
        capitalShare:
          user.businessInfo && user.businessInfo.qualifyingRequirementsByYear[0]
            ? user.businessInfo.qualifyingRequirementsByYear[0].capitalShare
            : 0,
        accessLevel:
          user.accessLevel.includes("EC") ||
          user.accessLevel.includes("WWG") ||
          (user.businessInfo.qualifyingRequirementsByYear &&
            user.businessInfo.qualifyingRequirementsByYear.length > 0 &&
            user.businessInfo.qualifyingRequirementsByYear.find(
              (yearItem) => yearItem.year === selectedYear
            ) &&
            user.businessInfo.qualifyingRequirementsByYear.find(
              (yearItem) => yearItem.year === selectedYear
            ).isExecutiveCommittee)
            ? "EC"
            : user.accessLevel.includes("MT2")
            ? "MT2"
            : user.accessLevel.includes("MT1") && "MT1",
        votingMember:
          user.businessInfo && user.businessInfo.qualifyingRequirementsByYear[0]
            ? user.businessInfo.qualifyingRequirementsByYear[0].isVotingMember
            : false,
        executiveCommittee:
          user.businessInfo && user.businessInfo.qualifyingRequirementsByYear[0]
            ? user.businessInfo.qualifyingRequirementsByYear[0]
                .isExecutiveCommittee
            : false,
        yearExecutiveCommitteeQualified: user.businessInfo
          ? user.businessInfo.yearExecutiveCommitteeQualified
          : "",
        committees:
          user.profile.committees &&
          user.profile.committees.map((a) => {
            return {
              committeeName: a.committeeName,
              yearStarted: a.yearStarted,
            };
          }),
        committeesB:
          user.profileB && user.profileB.committees
            ? user.profileB.committees
            : [],
        iboB: user.profileB
          ? {
              ...user.profileB,
              birthday: null,
              birthdate: user.profileB.birthday
                ? moment.utc(user.profileB.birthday).format("MMMM DD, YYYY")
                : "",
              anniversary: user.profileB.anniversary
                ? moment.utc(user.profileB.anniversary).format("MMMM DD, YYYY")
                : "",
            }
          : null,
      };
      setDiamondInfoModal(modalData);
      setIsOpen(true);
    }
  };

  const [originalData, setOriginalData] = useState(null);

  // Number of diamonds that are voting members
  const [votingCount, setVotingCount] = useState(0);
  // Determines open dropdown
  const [currentTab, setCurrentTab] = useState("");
  // DIAMOND SORTING
  // Name sorts by last name.
  const [sortFilter, setSortFilter] = useState({
    filter: "name",
    reverse: false,
  });
  // Sorted data
  const [editedDiamonds, setEditedDiamonds] = useState([]);
  // Determines if form is being edited
  const [isEditView, setIsEditView] = useState(false);
  // Determines AddYearModal open state
  const [isAdd, setIsAdd] = useState(false);

  const [isOverrideYear, setIsOverrideYear] = useState(false);

  // creates copy of original data for reseting state
  useEffect(() => {
    data && setOriginalData(data.users);
  }, [data]);

  // Update editedDiamonds and votingCount when data from API is retrieved
  useEffect(() => {
    if (data && data.users) {
      setVotingCount(data.votingMembersCount);
      setEditedDiamonds(
        data.users.sort((a, b) =>
          a.profile.lastName
            .toLowerCase()
            .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
              sensitivity: "base",
            })
        )
      );
    }
  }, [data, selectedYear]);

  // set year list array based on initial get call
  useEffect(() => {
    if (data && data.imports) {
      var yearArray = data.imports.map((imp) => `${imp.importYear}`);
      yearArray.sort((a, b) => {
        return a - b;
      });
      setYearList(yearArray);
    }
  }, [data]);

  const diamonds = useMemo(() => {
    if (currentTab == "diamonds") {
      return editedDiamonds.filter((a) => !a.accessLevel.includes("GUEST"));
    } else if (currentTab == "guests") {
      return editedDiamonds.filter((a) => a.accessLevel.includes("GUEST"));
    }
    return [];
  }, [currentTab, editedDiamonds, sortFilter]);
  // console.log(yearList);
  // set selected year as top year (which should default to currently selected year based on API call)
  useMemo(() => {
    if (yearList.length > 0 && data && !isEditView && !isOverrideYear) {
      yearList.includes(
        String(data.imports.find((imp) => imp.activatedDate).importYear)
      )
        ? setSelectedYear(
            String(data.imports.find((imp) => imp.activatedDate).importYear)
          )
        : setSelectedYear(yearList[0]);
    }
  }, [yearList, data, isEditView]);

  const handleSort = (newFilter) => {
    var newSortDiamonds = [];
    // name sort
    if (newFilter === "name") {
      if (sortFilter.filter === "name" && !sortFilter.reverse) {
        newSortDiamonds = editedDiamonds.sort((a, b) =>
          b.profile.lastName
            .toLowerCase()
            .localeCompare(a.profile.lastName.toLowerCase(), undefined, {
              sensitivity: "base",
            })
        );
      } else {
        newSortDiamonds = editedDiamonds.sort((a, b) =>
          a.profile.lastName
            .toLowerCase()
            .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
              sensitivity: "base",
            })
        );
      }
    }
    // gar sort
    if (newFilter === "gar") {
      if (sortFilter.filter === "gar" && !sortFilter.reverse) {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            a.businessInfo.qualifyingRequirementsByYear.filter(
              (info) => Number(info.year) === Number(selectedYear)
            )[0].garPoints -
              b.businessInfo.qualifyingRequirementsByYear.filter(
                (info) => Number(info.year) === Number(selectedYear)
              )[0].garPoints ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      } else {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            b.businessInfo.qualifyingRequirementsByYear.filter(
              (info) => Number(info.year) === Number(selectedYear)
            )[0].garPoints -
              a.businessInfo.qualifyingRequirementsByYear.filter(
                (info) => Number(info.year) === Number(selectedYear)
              )[0].garPoints ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      }
    }
    // i gar sort
    if (newFilter === "integratedGar") {
      if (sortFilter.filter === "integratedGar" && !sortFilter.reverse) {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            a.businessInfo.qualifyingRequirementsByYear.filter(
              (info) => Number(info.year) === Number(selectedYear)
            )[0].integratedGarPoints -
              b.businessInfo.qualifyingRequirementsByYear.filter(
                (info) => Number(info.year) === Number(selectedYear)
              )[0].integratedGarPoints ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      } else {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            b.businessInfo.qualifyingRequirementsByYear.filter(
              (info) => Number(info.year) === Number(selectedYear)
            )[0].integratedGarPoints -
              a.businessInfo.qualifyingRequirementsByYear.filter(
                (info) => Number(info.year) === Number(selectedYear)
              )[0].integratedGarPoints ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      }
    }
    // pin sort
    if (newFilter === "pinWearing") {
      if (sortFilter.filter === "pinWearing" && !sortFilter.reverse) {
        newSortDiamonds = editedDiamonds.sort((a, b) => {
          const aPinWearing =
            a.businessInfo?.qualifyingRequirementsByYear?.[0]?.pinWearing || "";
          const bPinWearing =
            b.businessInfo?.qualifyingRequirementsByYear?.[0]?.pinWearing || "";

          const compareResult = bPinWearing
            .toLowerCase()
            .localeCompare(aPinWearing.toLowerCase(), undefined, {
              sensitivity: "base",
            });

          if (compareResult !== 0) {
            return compareResult;
          }

          return a.profile.lastName
            .toLowerCase()
            .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
              sensitivity: "base",
            });
        });
      } else {
        newSortDiamonds = editedDiamonds.sort((a, b) => {
          const aPinWearing =
            a.businessInfo?.qualifyingRequirementsByYear?.[0]?.pinWearing || "";
          const bPinWearing =
            b.businessInfo?.qualifyingRequirementsByYear?.[0]?.pinWearing || "";

          const compareResult = aPinWearing
            .toLowerCase()
            .localeCompare(bPinWearing.toLowerCase(), undefined, {
              sensitivity: "base",
            });

          if (compareResult !== 0) {
            return compareResult;
          }

          return a.profile.lastName
            .toLowerCase()
            .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
              sensitivity: "base",
            });
        });
      }
    }
    // ec sort
    if (newFilter === "executiveCommittee") {
      if (sortFilter.filter === "executiveCommittee" && !sortFilter.reverse) {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            a.businessInfo.qualifyingRequirementsByYear.filter(
              (info) => Number(info.year) === Number(selectedYear)
            )[0].isExecutiveCommittee -
              b.businessInfo.qualifyingRequirementsByYear.filter(
                (info) => Number(info.year) === Number(selectedYear)
              )[0].isExecutiveCommittee ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      } else {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            b.businessInfo.qualifyingRequirementsByYear.filter(
              (info) => Number(info.year) === Number(selectedYear)
            )[0].isExecutiveCommittee -
              a.businessInfo.qualifyingRequirementsByYear.filter(
                (info) => Number(info.year) === Number(selectedYear)
              )[0].isExecutiveCommittee ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      }
    }
    // vm sort
    if (newFilter === "votingMember") {
      if (sortFilter.filter === "votingMember" && !sortFilter.reverse) {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            a.businessInfo.qualifyingRequirementsByYear.filter(
              (info) => Number(info.year) === Number(selectedYear)
            )[0].isVotingMember -
              b.businessInfo.qualifyingRequirementsByYear.filter(
                (info) => Number(info.year) === Number(selectedYear)
              )[0].isVotingMember ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      } else {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            b.businessInfo.qualifyingRequirementsByYear.filter(
              (info) => Number(info.year) === Number(selectedYear)
            )[0].isVotingMember -
              a.businessInfo.qualifyingRequirementsByYear.filter(
                (info) => Number(info.year) === Number(selectedYear)
              )[0].isVotingMember ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      }
    }
    // cap share sort
    if (newFilter === "capitalShare") {
      if (sortFilter.filter === "capitalShare" && !sortFilter.reverse) {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            a.businessInfo.qualifyingRequirementsByYear.filter(
              (info) => Number(info.year) === Number(selectedYear)
            )[0].capitalShare -
              b.businessInfo.qualifyingRequirementsByYear.filter(
                (info) => Number(info.year) === Number(selectedYear)
              )[0].capitalShare ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      } else {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            b.businessInfo.qualifyingRequirementsByYear.filter(
              (info) => Number(info.year) === Number(selectedYear)
            )[0].capitalShare -
              a.businessInfo.qualifyingRequirementsByYear.filter(
                (info) => Number(info.year) === Number(selectedYear)
              )[0].capitalShare ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      }
    }
    // year qual sort
    if (newFilter === "yearQualified") {
      if (sortFilter.filter === "yearQualified" && !sortFilter.reverse) {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            a.businessInfo.yearDiamondQualified -
              b.businessInfo.yearDiamondQualified ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      } else {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            b.businessInfo.yearDiamondQualified -
              a.businessInfo.yearDiamondQualified ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      }
    }
    // mt group sort
    if (newFilter === "managementTeamGroup") {
      const sortPriority = {
        EC: 1,
        MT1: 2,
        MT2: 3,
        RO: 4,
        NONE: 5,
        GUEST: 6,
      };

      if (sortFilter.filter === "managementTeamGroup" && !sortFilter.reverse) {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            sortPriority[formatDiamond(a).accessLevel] <
              sortPriority[formatDiamond(b).accessLevel] ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      } else {
        newSortDiamonds = editedDiamonds.sort(
          (a, b) =>
            sortPriority[formatDiamond(b).accessLevel] <
              sortPriority[formatDiamond(a).accessLevel] ||
            a.profile.lastName
              .toLowerCase()
              .localeCompare(b.profile.lastName.toLowerCase(), undefined, {
                sensitivity: "base",
              })
        );
      }
    }

    setEditedDiamonds(newSortDiamonds);

    setSortFilter({
      filter: newFilter,
      reverse: newFilter === sortFilter.filter ? !sortFilter.reverse : false,
    });
  };

  const capitalizeFirstLetter = (string) => {
    var _string = string.toLowerCase();
    return _string.charAt(0).toUpperCase() + _string.slice(1);
  };

  // Destructure diamond for DiamondListItem
  const formatDiamond = (diamond) => {
    const currentInfo =
      diamond.businessInfo.qualifyingRequirementsByYear.filter(
        (info) => parseInt(info.year) === parseInt(selectedYear)
      )[0];
    if (currentInfo) {
      let actualAccessLevel = // diamond.calculatedAccessLevel.includes("EC")
        // ? "EC" //override should not have EC
        diamond.calculatedAccessLevel.includes("MT1")
          ? "MT1"
          : diamond.calculatedAccessLevel.includes("MT2")
          ? "MT2"
          : diamond.accessLevel.includes("GUEST") //calculated doesn't have GUEST
          ? "GUEST"
          : "NONE";

      let accessLevel = diamond.overrideAccessLevel
        ? diamond.overrideAccessLevel
        : actualAccessLevel;

      return {
        name: `${capitalizeFirstLetter(
          diamond.profile.firstName
        )} ${capitalizeFirstLetter(diamond.profile.lastName)}`,
        garPoints: currentInfo.garPoints,
        integratedGar: currentInfo.integratedGarPoints,
        pinWearing: currentInfo.pinWearing,
        executiveCommittee: currentInfo.isExecutiveCommittee,
        votingMember: currentInfo.isVotingMember,
        capitalShare: currentInfo.capitalShare,
        yearQualified: String(diamond.businessInfo.yearDiamondQualified),
        id: diamond._id,
        accessLevel: accessLevel,
        actualAccessLevel: actualAccessLevel,
        overrideAccessLevel: diamond.overrideAccessLevel,
      };
    }
  };

  // Handles edit cancel
  const onCancel = () => {
    setIsEditInfo(false);
    setSortFilter({
      filter: "name",
      reverse: false,
    });
  };

  // resets state on page change
  const onReset = () => {
    setEditedDiamonds([]);
    setSelectedYear(date.getFullYear());
    setIsAdd(false);
    setIsEditInfo(false);
    setSortFilter({
      filter: "name",
      reverse: false,
    });
  };
  const [isUpdatingDiamond, setIsUpdatingDiamond] = useState(false);
  // handles settings edit API call
  const handleSettingsPut = () => {
    var _data = editedDiamonds.map((diamond) => {
      var index = diamond.businessInfo.qualifyingRequirementsByYear.findIndex(
        (byYear) => byYear.year === parseInt(selectedYear)
      );
      if (diamond.businessInfo.qualifyingRequirementsByYear[index]) {
        var diamondInfo =
          diamond.businessInfo.qualifyingRequirementsByYear[index];
        return {
          _id: diamond._id,
          garPoints: parseFloat(diamondInfo.garPoints),
          integratedGarPoints: parseFloat(diamondInfo.integratedGarPoints),
          diamondRankGar: diamondInfo.diamondRankGar,
          diamondRankWWG: diamondInfo.diamondRankWWG,
          pinWearing: diamondInfo.pinWearing,
          isExecutiveCommittee: diamondInfo.isExecutiveCommittee,
          isVotingMember: diamondInfo.isVotingMember,
          capitalShare: parseFloat(diamondInfo.capitalShare),
          yearDiamondQualified: diamondInfo.yearDiamondQualified,
          overrideAccessLevel:
            diamond.businessInfo.overrideAccessLevel ||
            diamond.overrideAccessLevel,
        };
      } else return null;
    });
    setIsUpdatingDiamond(true);
    settingsPut
      .mutateAsync({
        maxVotingMembers: votingCount,
        data: _data,
      })
      .then(() => {
        setIsUpdatingDiamond(false);
      });
    setIsEditInfo(false);
  };

  const onSaveUser = async (e) => {
    var data = e.picture;
    var mimeType = data.substring("data:".length, data.indexOf(";base64"));

    var dataB = e.iboB && e.iboB.photoUrl ? e.iboB.photoUrl : null;
    var mimeTypeB =
      dataB &&
      dataB.length > 0 &&
      dataB.substring("data:".length, dataB.indexOf(";base64"));

    userEditPut.mutate(
      {
        userId: e.userId,
        iboNumber: e.iboNum,
        accessLevel: e.accessLevel,
        profile: {
          spouse: e.spouse,
          birthday: e.birthdate,
          anniversary: e.anniversary,
          ...(e.isPhotoChanged && {
            photoUrl: {
              name: "New Profile Picture",
              data: data,
              mimetype: mimeType,
            },
          }),
        },
        businessInfo: {
          uplineDiamond: e.uplineDiamond,
          yearVotingMemberQualified: e.votingMemberYear,
          yearExecutiveCommitteeQualified: e.yearExecutiveCommitteeQualified,
          qualifyingRequirementsByYear: [
            {
              year: e.yearQualifyingRequirements,
            },
          ],
        },
        gracePeriod: {
          isGracePeriod: e.gracePeriod,
          startedOn: e.gracePeriodStarted,
        },
        eventInfo: {
          hasFamilyReunion: e.familyReunion,
          firstFamilyReunion: e.firstFamilyReunion,
          attendanceAtLastFamilyReunion: e.familyReunionAttendance,
        },
        profileB: {
          ...e.iboB,
          ...(e.isPhotoBChanged && {
            photoUrl: dataB
              ? {
                  name: "New Profile Picture",
                  data: dataB,
                  mimetype: mimeTypeB,
                }
              : null,
          }),
        },
      },
      {
        onSettled: () => {
          getSettings(selectedYear);
          refetchDashboard();
        },
      }
    );
    // TODO: Add API to Edit User Committees After API Update
  };

  async function onSwapIBO(e) {
    setIsOpen(false);
    setDiamondInfoModal(null);
    setIsRefetchingDashData(true);

    swapIBOPost.mutateAsync(e.userId).then(async () => {
      await getSettings(selectedYear);
      await refetchDashboard();
      setIsRefetchingDashData(false);
    });
  }
  // console.log(diamonds);
  return (
    <>
      {isOpen && diamondInfoModal && (
        <div className={styles.diamondCardModal}>
          <DiamondCardModal
            diamondInfo={diamondInfoModal}
            isOpen={true}
            onClose={() => {
              setIsOpen(false);
              setDiamondInfoModal(null);
            }}
            onSwap={(diamondModalInfo) => {
              onSwapIBO(diamondModalInfo);
            }}
            onSave={async (e) => {
              await onSaveUser(e);
              setTimeout(() => {
                getSettings(selectedYear);
                refetchDashboard();
              }, 2000);
            }}
          />
        </div>
      )}
      {addUserModalOpen && (
        <AddUserModal
          isOpen={true}
          onCollapse={() => {
            setAddUserModalOpen(false);
          }}
          onAdd={({ user, accessLevels, iboNumber }) => {
            settingsAddUser
              .mutateAsync({ user, accessLevels, iboNumber })
              .then(() => {
                getSettings(selectedYear);
              });
          }}
        />
      )}
      {pdfModalIsOpen && (
        <div className={styles.pdfModalContainer}>
          <div className={styles.pdfModal}>
            <div className={styles.pdfContentContainer}>
              <div className={styles.pdfHeader}>
                <AgendaIcon />
                PDF Export
              </div>
              <div className={styles.sortSelect}>
                Sort By
                <div className={styles.selectContainer}>
                  <Select
                    placeholder={pdfSortSelect}
                    selectedOption={pdfSortSelect}
                    options={[
                      "Diamond Name",
                      "GAR Points",
                      "Integrated GAR",
                      "Voting Member",
                      "Capital Share",
                      "Year Qualified",
                    ]}
                    onChange={(e) => setPdfSortSelect(e)}
                  />
                </div>
              </div>
              <div className={styles.radioSelect}>
                Include Columns
                <div className={styles.radioContainer}>
                  <div className={styles.radio}>
                    <input
                      type="radio"
                      value={"All"}
                      readOnly
                      onClick={() => setPdfColumnSelect([])}
                      checked={pdfColumnSelect.length === 0}
                      className={classNames(styles["radio-button"])}
                    />
                    <label className={classNames(styles.label)}>All</label>
                  </div>
                  <div className={styles.radioRow}>
                    <div className={styles.radioCol}>
                      {radioOptions1.map((option) => (
                        <div className={styles.radio}>
                          <input
                            type="radio"
                            value={option}
                            readOnly
                            onClick={() =>
                              pdfColumnSelect.includes(option)
                                ? setPdfColumnSelect(
                                    pdfColumnSelect.filter(
                                      (select) => select !== option
                                    )
                                  )
                                : setPdfColumnSelect([
                                    ...pdfColumnSelect,
                                    option,
                                  ])
                            }
                            checked={pdfColumnSelect.includes(option)}
                            className={classNames(styles["radio-button"])}
                          />
                          <label className={classNames(styles.label)}>
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className={styles.radioCol}>
                      {radioOptions2.map((option) => (
                        <div className={styles.radio}>
                          <input
                            type="radio"
                            value={option}
                            readOnly
                            onClick={() =>
                              pdfColumnSelect.includes(option)
                                ? setPdfColumnSelect(
                                    pdfColumnSelect.filter(
                                      (select) => select !== option
                                    )
                                  )
                                : setPdfColumnSelect([
                                    ...pdfColumnSelect,
                                    option,
                                  ])
                            }
                            checked={pdfColumnSelect.includes(option)}
                            className={classNames(styles["radio-button"])}
                          />
                          <label className={classNames(styles.label)}>
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.buttonRow}>
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setPdfModalIsOpen(false);
                    setPdfSortSelect("Diamond Name");
                    setPdfColumnSelect([]);
                  }}
                >
                  Cancel
                </button>
                <>
                  <BlobProvider
                    document={
                      <SettingsPdf
                        date={selectedYear}
                        pdfColumnSelect={pdfColumnSelect}
                        pdfSortSelect={pdfSortSelect}
                        diamondSettings={diamonds
                          .filter((diamond) => diamond.accessLevel.length !== 0)
                          .map((diamond) => {
                            if (
                              diamond.businessInfo.qualifyingRequirementsByYear.some(
                                (byYear) =>
                                  parseInt(byYear.year) ===
                                  parseInt(selectedYear)
                              )
                            ) {
                              return diamond;
                            }
                          })}
                      />
                    }
                    fileName={`${selectedYear}_settings`}
                  >
                    {({ blob, url, loading, error }) =>
                      loading ? (
                        <button className={styles.loadingPdf}>
                          Generating
                        </button>
                      ) : (
                        <a href={url} target="_blank" rel="noreferrer">
                          <button
                            className={styles.downloadButton}
                            onClick={() => {
                              if (!loading) {
                                setPdfModalIsOpen(false);
                                setPdfSortSelect("Diamond Name");
                                setPdfColumnSelect([]);
                              }
                            }}
                          >
                            Download
                          </button>
                        </a>
                      )
                    }
                  </BlobProvider>
                </>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={classNames(styles["page-container"])}>
        <div className={classNames(styles.header)}>
          <Header
            title="Settings"
            icon={SettingsIcon}
            isLock={true}
            isHide={isHideInfo}
            setToggleInfo={setToggleInfo}
            onRefresh={handleRefresh}
            isLoading={isRefreshing || isDashboardFetching}
          />
        </div>
        <div className={styles.grid}>
          <div
            className={classNames(styles.transition, styles.menu, {
              [styles.hidden]: currentTab !== "",
            })}
          >
            <MonthLink
              month="Manage Diamonds"
              year=" "
              onClick={() => setCurrentTab("diamonds")}
            />
            <MonthLink
              month="Manage Guests"
              year=" "
              onClick={() => setCurrentTab("guests")}
            />
            <MonthLink
              month="Notifications"
              year=" "
              onClick={() => setCurrentTab("notifications")}
            />
          </div>
          <div
            className={classNames(styles.transition, styles.content, {
              [styles.hidden]: currentTab === "",
            })}
          >
            <button
              className={styles["nav-button"]}
              onClick={() => {
                setCurrentTab("");
                onReset();
              }}
            >
              <span className={styles["left-arrow"]} />
              <span>Settings</span>
            </button>
            {!isHideInfo && (
              <section
                className={classNames(styles.transition, styles.section, {
                  [styles.hidden]:
                    currentTab !== "diamonds" && currentTab !== "guests",
                })}
              >
                <Card
                  header={
                    currentTab === "diamonds"
                      ? "Manage Diamonds"
                      : currentTab === "guests"
                      ? "Manage Guests"
                      : "Notifications"
                  }
                  children={
                    isUpdatingDiamond ? (
                      <div className={styles.isSaving}>
                        <p> Saving...</p>
                        <CircleLoader />
                      </div>
                    ) : (
                      <div className={styles["section-card"]}>
                        <div className={styles["diamond-header"]}>
                          <div className={styles["diamond-select"]}>
                            {isEditView ? (
                              <>
                                <Select
                                  options={[
                                    ...useYearList,
                                    "- - - - - - - - - - - - - - -",
                                    "Add Year",
                                  ]}
                                  selectedOption={selectedYear}
                                  onChange={(value) => {
                                    if (
                                      value !== "" &&
                                      value !==
                                        "- - - - - - - - - - - - - - -" &&
                                      value !== "Add Year"
                                    ) {
                                      setStoreYearSelect(selectedYear);
                                      setSelectedYear(value);
                                      setCurrentYear(value);
                                      setIsOverrideYear(true);
                                    }
                                    if (value === "Add Year") {
                                      setIsAdd(true);
                                    }
                                  }}
                                />
                              </>
                            ) : (
                              <>
                                <span className={styles.year}>
                                  {selectedYear}
                                </span>
                                {!isEditView && (
                                  <span className={styles.activeLabel}>
                                    Active as of{" "}
                                    {yearImport && yearImport.activatedDate
                                      ? moment(yearImport.activatedDate).format(
                                          "M.DD.YYYY"
                                        )
                                      : "N/A"}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          {isEditView && (
                            <div
                              style={{
                                visibility: !readOnly ? "visible" : "hidden",
                              }}
                              className={styles.importButtonsContainer}
                            >
                              <div className={styles.importCol}>
                                <button
                                  disabled={
                                    settingsYearActivatePost.isLoading ||
                                    (yearImport &&
                                      yearImport.importYear >= currentYear)
                                      ? yearImport.isActivated
                                      : true
                                  }
                                  className={classNames(
                                    styles.transition,
                                    yearImport && !yearImport.isActivated
                                      ? styles["active"]
                                      : null
                                  )}
                                  onClick={() => {
                                    settingsYearActivatePost.mutate(
                                      selectedYear
                                    );
                                  }}
                                >
                                  {settingsYearActivatePost.isLoading
                                    ? "Activating..."
                                    : "Make Settings Year Active"}
                                </button>
                                <label className={styles.importButtonLabel}>
                                  Active as of{" "}
                                  {yearImport && yearImport.activatedDate
                                    ? moment(yearImport.activatedDate).format(
                                        "M.DD.YYYY"
                                      )
                                    : "N/A"}
                                </label>
                              </div>
                              <div className={styles.importCol}>
                                <button
                                  disabled={
                                    settingsYearImportDiamondsPost.isLoading
                                  }
                                  className={classNames(
                                    styles.transition,
                                    styles["active"]
                                  )}
                                  onClick={() => {
                                    settingsYearImportDiamondsPost.mutate(
                                      selectedYear
                                    );
                                  }}
                                >
                                  {settingsYearImportDiamondsPost.isLoading
                                    ? "Importing..."
                                    : "Import Diamonds"}
                                </button>
                                <label className={styles.importButtonLabel}>
                                  Last Imported{" "}
                                  {yearImport && yearImport
                                    ? moment(yearImport.importedDate).format(
                                        "M.DD.YYYY"
                                      )
                                    : "N/A"}
                                </label>
                              </div>
                              <div className={styles.importCol}>
                                <button
                                  disabled={
                                    settingsYearCalculatePost.isLoading ||
                                    yearImport
                                      ? yearImport.isCalculated
                                      : true
                                  }
                                  className={classNames(
                                    styles.transition,
                                    yearImport && !yearImport.isCalculated
                                      ? styles["active"]
                                      : null
                                  )}
                                  onClick={() => {
                                    settingsYearCalculatePost.mutate(
                                      selectedYear
                                    );
                                  }}
                                >
                                  {settingsYearCalculatePost.isLoading
                                    ? "Calculating..."
                                    : "Calculate Voting Members"}
                                </button>
                                <label className={styles.importButtonLabel}>
                                  Calculated{" "}
                                  {yearImport && yearImport.calculatedDate
                                    ? moment(yearImport.calculatedDate).format(
                                        "M.DD.YYYY"
                                      )
                                    : "N/A"}
                                </label>
                              </div>
                            </div>
                          )}
                          {!isEditInfo && (
                            <div className={styles["diamond-buttons"]}>
                              <span className={styles.buttonContainer}>
                                <CancelButton
                                  onClick={() => {
                                    setIsEditView(false);
                                    setSelectedYear(storeYearSelect);
                                    setCurrentYear(storeYearSelect);
                                  }}
                                  className={classNames(
                                    styles.transition,
                                    styles["diamond-button"],
                                    styles.cancel,
                                    {
                                      [styles.hidden]: !isEditView,
                                    }
                                  )}
                                />
                              </span>

                              <div
                                style={{
                                  visibility: !readOnly ? "visible" : "hidden",
                                }}
                                className={styles.saveButtonContainer}
                              >
                                {isEditView ? (
                                  <SaveButton
                                    className={classNames(
                                      styles.transition,
                                      styles["diamond-button"],
                                      {
                                        [styles.hidden]: !isEditView,
                                      }
                                    )}
                                    onClick={() => {
                                      setStoreYearSelect(null);
                                      setIsEditView(false);
                                    }}
                                  />
                                ) : (
                                  <button
                                    className={classNames(
                                      styles.transition,
                                      styles["diamond-button"],
                                      {
                                        [styles.hidden]:
                                          isEditView || !hasOwnersPermission,
                                      },
                                      styles.editInfoButton
                                    )}
                                    onClick={() => {
                                      setIsEditView(true);
                                    }}
                                  >
                                    Edit Settings
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className={styles["header-line"]} />

                        <div className={styles["diamond-subheader"]}>
                          <div className={styles["voting-members"]}>
                            <label htmlFor="voting-members">
                              VOTING MEMBERS:{" "}
                            </label>
                            <input
                              type="number"
                              disabled={!isEditInfo}
                              value={votingCount}
                              name="voting-members"
                              max={21}
                              onChange={(e) => {
                                var count = e.target.value;
                                if (count > 21) {
                                  count = 21;
                                }
                                setVotingCount(count);
                              }}
                            />
                            <p className={styles["quorum-container"]}>
                              QUORUM:{" "}
                              <span className={styles.quorum}>
                                {Math.round(votingCount * 0.7)}
                              </span>
                            </p>
                          </div>
                          {!isEditView && (
                            <div className={styles["diamond-buttons"]}>
                              <span className={styles.buttonContainer}>
                                <button
                                  className={classNames(styles.addGuestButton, {
                                    [styles.hidden]: !isEditInfo,
                                  })}
                                  onClick={() => {
                                    setAddUserModalOpen(true);
                                  }}
                                >
                                  Add New Guest
                                </button>

                                <CancelButton
                                  onClick={() => {
                                    setEditedDiamonds(originalData);
                                    setIsEditInfo(false);
                                    onCancel();
                                    setSortFilter({
                                      filter: "name",
                                      reverse: false,
                                    });
                                  }}
                                  className={classNames(
                                    styles.transition,
                                    styles["diamond-button"],
                                    styles.cancel,
                                    {
                                      [styles.hidden]: !isEditInfo,
                                    }
                                  )}
                                />
                                {isEditInfo && (
                                  <button
                                    className={styles.saveButton}
                                    onClick={() => {
                                      setPdfModalIsOpen(true);
                                    }}
                                  >
                                    Save as PDF
                                  </button>
                                )}
                              </span>

                              <div
                                style={{
                                  visibility: !readOnly ? "visible" : "hidden",
                                }}
                                className={styles.saveButtonContainer}
                              >
                                {isEditInfo ? (
                                  <SaveButton
                                    className={classNames(
                                      styles.transition,
                                      styles["diamond-button"],
                                      {
                                        [styles.hidden]: !isEditInfo,
                                      }
                                    )}
                                    onClick={handleSettingsPut}
                                  />
                                ) : (
                                  <button
                                    className={classNames(
                                      styles.transition,
                                      styles["diamond-button"],
                                      {
                                        [styles.hidden]:
                                          isEditInfo || !hasOwnersPermission,
                                      },
                                      styles.editInfoButton
                                    )}
                                    onClick={() => {
                                      setIsEditInfo(true);
                                    }}
                                  >
                                    Edit Info
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <AddYearModal
                          onClose={() => setIsAdd(false)}
                          isOpen={isAdd}
                          onAdd={(year) => {
                            if (
                              Number(year) <= Number(date.getFullYear() + 1)
                            ) {
                              if (!yearList.includes(Number(year))) {
                                settingsYearPost
                                  .mutateAsync({ year: year })
                                  .then(() => {
                                    if (Number(year) !== Number(selectedYear)) {
                                      setSelectedYear(Number(year));
                                    }
                                  });
                              }

                              setIsAdd(false);
                            } else {
                              alert(
                                "Please enter a valid year less than one year ahead of the calendar year."
                              );
                            }
                          }}
                        />
                        <div className={styles["body-container"]}>
                          <div className={styles["diamond-body"]}>
                            <div
                              className={classNames(
                                styles["diamond-body-label"],
                                sortFilter.filter === "name" && styles.selected
                              )}
                            >
                              <button
                                className={styles["filter-button-container"]}
                              >
                                <div
                                  className={classNames(
                                    styles["filter-button"],
                                    {
                                      [styles.reverse]:
                                        sortFilter.filter === "name" &&
                                        sortFilter.reverse,
                                    }
                                  )}
                                  onClick={() => handleSort("name")}
                                >
                                  <span>Diamond</span>
                                  <span
                                    className={classNames(
                                      styles.transition,
                                      styles["filled-down-arrow"]
                                    )}
                                  />
                                </div>
                              </button>
                            </div>
                            <p
                              className={classNames(
                                styles["diamond-body-label"],
                                sortFilter.filter === "gar" && styles.selected,
                                styles["filter-button"],
                                {
                                  [styles.reverse]:
                                    sortFilter.filter === "gar" &&
                                    sortFilter.reverse,
                                }
                              )}
                              onClick={() => handleSort("gar")}
                            >
                              <span>Gar Points</span>
                              <span
                                className={classNames(
                                  styles.transition,
                                  styles["filled-down-arrow"]
                                )}
                              />
                            </p>
                            <p
                              className={classNames(
                                styles["diamond-body-label"],
                                sortFilter.filter === "integratedGar" &&
                                  styles.selected,
                                styles["filter-button"],
                                {
                                  [styles.reverse]:
                                    sortFilter.filter === "integratedGar" &&
                                    sortFilter.reverse,
                                }
                              )}
                              onClick={() => handleSort("integratedGar")}
                            >
                              <span>Integrated Gar</span>
                              <span
                                className={classNames(
                                  styles.transition,
                                  styles["filled-down-arrow"]
                                )}
                              />
                            </p>
                            <p
                              className={classNames(
                                styles["diamond-body-label"],
                                sortFilter.filter === "pinWearing" &&
                                  styles.selected,
                                styles["filter-button"],
                                {
                                  [styles.reverse]:
                                    sortFilter.filter === "pinWearing" &&
                                    sortFilter.reverse,
                                }
                              )}
                              onClick={() => handleSort("pinWearing")}
                            >
                              <span>Pin Wearing</span>
                              <span
                                className={classNames(
                                  styles.transition,
                                  styles["filled-down-arrow"]
                                )}
                              />
                            </p>
                            <p
                              className={classNames(
                                styles["diamond-body-label"],
                                sortFilter.filter === "executiveCommittee" &&
                                  styles.selected,
                                styles["filter-button"],
                                {
                                  [styles.reverse]:
                                    sortFilter.filter ===
                                      "executiveCommittee" &&
                                    sortFilter.reverse,
                                }
                              )}
                              onClick={() => handleSort("executiveCommittee")}
                            >
                              <span>Executive Committee</span>
                              <span
                                className={classNames(
                                  styles.transition,
                                  styles["filled-down-arrow"]
                                )}
                              />
                            </p>
                            <p
                              className={classNames(
                                styles["diamond-body-label"],
                                sortFilter.filter === "votingMember" &&
                                  styles.selected,
                                styles["filter-button"],
                                {
                                  [styles.reverse]:
                                    sortFilter.filter === "votingMember" &&
                                    sortFilter.reverse,
                                }
                              )}
                              onClick={() => handleSort("votingMember")}
                            >
                              <span>Voting Member</span>
                              <span
                                className={classNames(
                                  styles.transition,
                                  styles["filled-down-arrow"]
                                )}
                              />
                            </p>
                            <p
                              className={classNames(
                                styles["diamond-body-label"],
                                sortFilter.filter === "capitalShare" &&
                                  styles.selected,
                                styles["filter-button"],
                                {
                                  [styles.reverse]:
                                    sortFilter.filter === "capitalShare" &&
                                    sortFilter.reverse,
                                }
                              )}
                              onClick={() => handleSort("capitalShare")}
                            >
                              <div className={styles["lock-container"]}>
                                <div className={styles.lock}>
                                  <span />
                                  <LockIcon />
                                  <span />
                                </div>
                              </div>
                              <span>Capital Share</span>
                              <span
                                className={classNames(
                                  styles.transition,
                                  styles["filled-down-arrow"]
                                )}
                              />
                            </p>
                            <p
                              className={classNames(
                                styles["diamond-body-label"],
                                sortFilter.filter === "yearQualified" &&
                                  styles.selected,
                                styles["filter-button"],
                                {
                                  [styles.reverse]:
                                    sortFilter.filter === "yearQualified" &&
                                    sortFilter.reverse,
                                }
                              )}
                              onClick={() => handleSort("yearQualified")}
                            >
                              <span>Year Qualified</span>
                              <span
                                className={classNames(
                                  styles.transition,
                                  styles["filled-down-arrow"]
                                )}
                              />
                            </p>
                            <p
                              className={classNames(
                                styles["diamond-body-label"],
                                sortFilter.filter === "managementTeamGroup" &&
                                  styles.selected,
                                styles["filter-button"],
                                {
                                  [styles.reverse]:
                                    sortFilter.filter ===
                                      "managementTeamGroup" &&
                                    sortFilter.reverse,
                                }
                              )}
                              onClick={() => handleSort("managementTeamGroup")}
                            >
                              <span>Management Team Group</span>
                              <span
                                className={classNames(
                                  styles.transition,
                                  styles["filled-down-arrow"]
                                )}
                              />
                            </p>
                            <p>{/*AlertIcon column*/}</p>
                          </div>
                          <div
                            className={classNames(
                              styles.transition,
                              styles["diamond-body"],
                              {
                                [styles.edit]: isEditInfo,
                              }
                            )}
                          >
                            {diamonds.length ? (
                              <ul className={styles.manageDiamondsList}>
                                {diamonds.map((diamond) => {
                                  if (
                                    diamond.businessInfo.qualifyingRequirementsByYear.some(
                                      (byYear) =>
                                        parseInt(byYear.year) ===
                                        parseInt(selectedYear)
                                    )
                                  ) {
                                    return (
                                      <li
                                        key={diamond._id}
                                        className={classNames(
                                          styles.listItem,
                                          styles["list-item"],
                                          styles.transition,
                                          {
                                            [styles["list-item-edit"]]:
                                              isEditInfo,
                                          }
                                        )}
                                      >
                                        <DiamondListItem
                                          onNameClick={() =>
                                            !isRefetchingDashData &&
                                            handleSetDiamond(diamond._id)
                                          }
                                          edit={isEditInfo}
                                          diamond={formatDiamond(diamond)}
                                          onChange={(value, property) => {
                                            // create deep clone of array of objects
                                            var _editedDiamonds = JSON.parse(
                                              JSON.stringify(editedDiamonds)
                                            );
                                            var newDiamonds =
                                              _editedDiamonds.map(
                                                (_diamond) => {
                                                  if (
                                                    diamond._id === _diamond._id
                                                  ) {
                                                    var newDiamond = {
                                                      ..._diamond,
                                                    };
                                                    newDiamond.businessInfo.qualifyingRequirementsByYear.filter(
                                                      (byYear) =>
                                                        Number(byYear.year) ===
                                                        Number(selectedYear)
                                                    )[0][property] = value;

                                                    if (
                                                      property ===
                                                      "overrideAccessLevel"
                                                    ) {
                                                      newDiamond.overrideAccessLevel =
                                                        value;
                                                    }

                                                    if (
                                                      property ===
                                                      "yearDiamondQualified"
                                                    ) {
                                                      newDiamond.businessInfo.yearDiamondQualified =
                                                        value;
                                                    }

                                                    return newDiamond;
                                                  }
                                                  return _diamond;
                                                }
                                              );
                                            setEditedDiamonds(newDiamonds);
                                          }}
                                          isWait={isRefetchingDashData}
                                        />
                                      </li>
                                    );
                                  }
                                })}
                              </ul>
                            ) : (
                              <div className={styles["empty-message"]}>
                                {status === "success"
                                  ? `No Diamonds For ${selectedYear}`
                                  : status === "loading"
                                  ? "Loading data..."
                                  : "Error retrieving data"}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  }
                />
              </section>
            )}
            {isHideInfo && currentTab !== "notifications" && (
              <div className={styles.privacyModeText}>
                This page is currently in Privacy Mode
              </div>
            )}
            <section
              className={classNames(styles.transition, styles.section, {
                [styles.hidden]: currentTab !== "notifications",
              })}
            >
              <Card
                header={currentTab === "notifications" && "Notifications"}
                children={
                  <div className={styles["section-card"]}>
                    <div className={styles["diamond-header"]}>
                      <div className={styles["diamond-select"]}>
                        <span className={styles.year}>Submitted Agendas</span>
                      </div>
                      {!isEditInfo && (
                        <div className={styles["diamond-buttons"]}>
                          <span className={styles.buttonContainer}>
                            <CancelButton
                              onClick={() => {
                                setIsEditView(false);
                                setSelectedYear(storeYearSelect);
                                setCurrentYear(storeYearSelect);
                              }}
                              className={classNames(
                                styles.transition,
                                styles["diamond-button"],
                                styles.cancel,
                                {
                                  [styles.hidden]: !isEditView,
                                }
                              )}
                            />
                          </span>

                          <div
                            style={{
                              visibility: !readOnly ? "visible" : "hidden",
                            }}
                            className={styles.saveButtonContainer}
                          >
                            {isEditView ? (
                              <SaveButton
                                className={classNames(
                                  styles.transition,
                                  styles["diamond-button"],
                                  {
                                    [styles.hidden]: !isEditView,
                                  }
                                )}
                                onClick={() => {
                                  setIsEditView(!isEditView);
                                  settingsAddAdminsToNotificationsPost.mutate({
                                    admins: listAdminsEditted,
                                  });
                                }}
                              />
                            ) : (
                              <button
                                className={classNames(
                                  styles.transition,
                                  styles["diamond-button"],
                                  {
                                    [styles.hidden]:
                                      isEditView || !hasOwnersPermission,
                                  },
                                  styles.editInfoButton
                                )}
                                onClick={() => {
                                  setIsEditView(true);
                                }}
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className={styles["header-line"]} />
                    <div className={styles["sub-header-text"]}>
                      These admins will be notified when a Diamond submits an
                      agenda.
                    </div>
                    <div className={styles["notification-table-container"]}>
                      <div
                        className={classNames(styles["notification-header"])}
                      >
                        <span>NAME</span>
                        <span>EMAIL ADDRESS</span>
                      </div>
                      {listAdmins.map((admin) => (
                        <NotificationInput
                          _id={admin._id}
                          name={admin.name}
                          email={admin.email}
                          editMode={isEditView}
                          onDelete={() => {
                            const newAdminList = listAdmins.filter(
                              (a) => a.email !== admin.email
                            );
                            setListAdmins(newAdminList);
                            adminDelete.mutate({
                              id: admin._id,
                            });
                          }}
                          onChange={(admin) => {
                            const newList = listAdmins.map((a) => {
                              if (a._id === admin._id) {
                                const edittedAdIdx =
                                  listAdminsEditted.findIndex(
                                    (ad) => ad._id === admin._id
                                  );
                                const tmp = [...listAdminsEditted];
                                if (edittedAdIdx !== -1) {
                                  tmp[edittedAdIdx] = admin;
                                } else {
                                  tmp.push(admin);
                                }
                                setListAdminsEditted(tmp);
                                return admin;
                              }
                              return a;
                            });
                            setListAdmins(newList);
                          }}
                        />
                      ))}
                    </div>
                    {isEditView && (
                      <div>
                        <Button
                          className={classNames(styles.addNameBtn)}
                          onClick={() => {
                            setListAdmins([
                              ...listAdmins,
                              { name: "", email: "", _id: null },
                            ]);
                          }}
                        >
                          ADD NAME
                        </Button>
                      </div>
                    )}
                  </div>
                }
              />
            </section>
            {/* <section
              className={classNames(styles.transition, styles.section, {
                [styles.hidden]: currentTab !== "guests",
              })}
            >
              <Card
                header="Manage Guests"
                children={
                  <div
                    className={classNames(
                      styles["section-card"],
                      styles.guests
                    )}
                  >
                    <p className={styles["guests-text"]}>
                      This page is where you can give a guest a membership
                      account and read only view of the Management Team app.
                    </p>
                  </div>
                }
              />
            </section> */}
          </div>
        </div>
      </div>
    </>
  );
}
